import dayjs, { Dayjs } from 'dayjs';
import { ComplimentaryPeriodSectionValues } from 'src/components/AddComplimentaryPeriodModal/add-complimentary-period-form';
import {
  AddComplimentaryPeriodFormField,
  COMPLIMENTARY_PERIOD_SECTION,
  DEFAULT_CPP_LENGTH_DAYS,
  getCppLengthList,
  getCppOptionFromList,
  TODAY_DATE,
} from 'src/components/AddComplimentaryPeriodModal/const';
import { prepareCppData } from 'src/components/AddComplimentaryPeriodModal/util';
import { PreSoldMembershipSectionValues } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-form';
import {
  AddPreSoldMembershipFormFields,
  PRE_SOLD_MEMBERSHIP_SECTION,
  preparePreSoldMembershipData,
  preSoldLengths,
} from 'src/components/AddPreSoldMembershipModal/util';
import { Address, Project, SubmitDay1Payload } from 'src/graphql';

export enum SubmitDay1FormField {
  Day1Date = 'day1Date',
  SendEducationalEmails = 'sendEducationalEmails',
  ProvideComplimentaryPeriod = 'provideComplimentaryPeriod',
  AddPreSoldMembership = 'addPreSoldMembership',
  MembershipSalesNotes = 'membershipSalesNotes',
}

export interface SubmitDay1FormValues
  extends ComplimentaryPeriodSectionValues,
    PreSoldMembershipSectionValues {
  [SubmitDay1FormField.Day1Date]: Dayjs | null;
  [SubmitDay1FormField.SendEducationalEmails]: boolean;
  [SubmitDay1FormField.ProvideComplimentaryPeriod]: boolean;
  [SubmitDay1FormField.AddPreSoldMembership]: boolean;
  [SubmitDay1FormField.MembershipSalesNotes]: string;
}

export interface SubmitDay1VisibleFields {
  sendEducationalEmails: boolean;
  complimentaryPeriod: boolean;
  preSoldMembership: boolean;
}

const DEFAULT_SEND_EDUCATIONAL_EMAILS = true;

export function getSubmitDay1DefaultValues(
  project?: Pick<Project, 'estimatedDay1Date'> | null,
  address?: Pick<Address, 'membershipSalesNotes' | 'preSoldMemberships'> | null,
): SubmitDay1FormValues {
  const day1Date = dayjs(project?.estimatedDay1Date || TODAY_DATE);
  const preSoldMembership = address?.preSoldMemberships?.[0];

  return {
    [SubmitDay1FormField.Day1Date]: day1Date,
    [SubmitDay1FormField.SendEducationalEmails]: DEFAULT_SEND_EDUCATIONAL_EMAILS,
    [SubmitDay1FormField.ProvideComplimentaryPeriod]: true,
    [SubmitDay1FormField.AddPreSoldMembership]: Boolean(
      preSoldMembership && !preSoldMembership.isScheduled,
    ),
    [SubmitDay1FormField.MembershipSalesNotes]: address?.membershipSalesNotes || '',
    [COMPLIMENTARY_PERIOD_SECTION]: {
      [AddComplimentaryPeriodFormField.PartnerProductId]: '',
      [AddComplimentaryPeriodFormField.CppExpirationDate]: getCppOptionFromList(
        getCppLengthList(day1Date),
        DEFAULT_CPP_LENGTH_DAYS,
      )!.value,
    },
    [PRE_SOLD_MEMBERSHIP_SECTION]: {
      [AddPreSoldMembershipFormFields.PartnerProductId]: '',
      [AddPreSoldMembershipFormFields.Price]: 0,
      [AddPreSoldMembershipFormFields.LengthInMonths]: preSoldLengths[0].value,
    },
  };
}

export interface PrepareSubmitDay1DataParams {
  project: Project;
  address: Address;
  values: SubmitDay1FormValues;
  visibleFields: SubmitDay1VisibleFields;
}

// the form unregisters hidden fields, so a hidden field is read from its visibility, not from its value
export function prepareSubmitDay1Data({
  project,
  address,
  values,
  visibleFields,
}: PrepareSubmitDay1DataParams): SubmitDay1Payload {
  return {
    projectId: project.projectId,
    addressId: address.addressId,
    partnerId: address.partnerId ?? project.partnerId,
    day1Date: values[SubmitDay1FormField.Day1Date]?.format('YYYY-MM-DD') || '',
    membershipSalesNotes: values[SubmitDay1FormField.MembershipSalesNotes],
    sendEducationalEmails: visibleFields.sendEducationalEmails
      ? values[SubmitDay1FormField.SendEducationalEmails]
      : DEFAULT_SEND_EDUCATIONAL_EMAILS,
    cpp: visibleFields.complimentaryPeriod
      ? prepareCppData(values[COMPLIMENTARY_PERIOD_SECTION])
      : undefined,
    psm: visibleFields.preSoldMembership
      ? preparePreSoldMembershipData(values[PRE_SOLD_MEMBERSHIP_SECTION])
      : undefined,
  };
}
